import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Gauge, Code2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Magnetic } from "../Magnetic";
import { DeferredContactModal } from "../DeferredContactModal";
import { useTranslation } from "react-i18next";
import { BoltyWrapper } from "@/components/ui/BoltyWrapper";
// The WebGL fluid is pure decoration behind the hero: the LCP element is the
// heading, not the canvas. Loading it lazily keeps its shader code out of the
// entry bundle and off the critical path. The fallback below paints the same warm
// glow immediately, so the background is never blank while the chunk loads.
const InkFlowField = React.lazy(() =>
  import("../InkFlowField").then((m) => ({ default: m.InkFlowField })),
);

export function HomeHero() {
  const { t } = useTranslation();
  const [showFlow, setShowFlow] = React.useState(false);

  React.useEffect(() => {
    // The canvas is decorative. Let the LCP text settle before starting WebGL
    // work that would otherwise force a layout during the first paint.
    const timer = window.setTimeout(() => setShowFlow(true), 6000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <section className="relative pt-16 pb-20 md:pt-24 md:pb-32 overflow-hidden min-h-[85vh] flex items-center">
      {/* Fluid on desktop, lightweight animated fallback on other devices. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none bg-[radial-gradient(60%_60%_at_50%_40%,rgba(191,38,11,0.18),transparent_70%)]"
      />
      {showFlow && <React.Suspense fallback={null}>
        <InkFlowField
            colors={["#BF260B", "#FF4500", "#FFAA00", "#FF0055", "#9333EA"]}
            speed={80}
            dissipation={42}
            swirl={28}
            drift={18}
            glow="rgba(191, 38, 11, 0.15)"
            cursor={{ force: 65, reach: 45 }}
          />
      </React.Suspense>}

      {/* Animated Grid Backdrop */}
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none">
        <div className="absolute inset-0 hero-grid" />
      </div>

      {/* Background ambient glow */}
      <div className="absolute top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/15 rounded-full blur-[140px] opacity-40 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10 pointer-events-auto">
        <div className="max-w-4xl mx-auto text-center">
          {/* Studio Badge */}
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-border/80 bg-card/60 backdrop-blur-md mb-6 sm:mb-8 shadow-xs">
            <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-semibold tracking-wide text-foreground/90 uppercase flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary-ink" /> {t('home_modern.hero.badge')}
            </span>
          </div>

          {/* Main Headline (Instant LCP & 100% SEO Crawler Discovery) */}
          <h1 className="text-2xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6 sm:mb-8 leading-[1.15] sm:leading-[1.08] text-foreground font-heading break-words">
            {t('home_modern.hero.h1_prefix')}{" "}
            <span className="bg-gradient-to-r from-primary via-orange-500 to-amber-500 bg-clip-text text-transparent">
              {t('home_modern.hero.h1_highlight')}
            </span>{" "}
            {t('home_modern.hero.h1_suffix')}
          </h1>

          {/* Subtitle - Instant LCP Element */}
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed font-normal">
            {t('home_modern.hero.desc')}
          </p>

          {/* Call to Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16 w-full max-w-md mx-auto sm:max-w-none">
            <Magnetic strength={0.15}>
              <BoltyWrapper size="lg" className="w-full sm:w-auto">
                <DeferredContactModal
                  defaultPack="Croissance"
                  defaultMessage={t('contact_extra.default_project_msg', "Bonjour, j'aimerais échanger sur la création / refonte de mon site web.")}
                >
                  <Button size="lg" className="rounded-full px-8 h-12 sm:h-14 text-sm sm:text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all w-full sm:w-auto">
                    {t('home_modern.hero.cta_launch')} <ArrowRight className="ms-2 w-4 h-4 rtl:rotate-180" />
                  </Button>
                </DeferredContactModal>
              </BoltyWrapper>
            </Magnetic>
            <Magnetic strength={0.15}>
              <BoltyWrapper size="lg" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 h-12 sm:h-14 text-sm sm:text-base font-medium border-border/80 hover:bg-muted/50 backdrop-blur-sm w-full sm:w-auto"
                  render={<Link to="/audit" />}
                  nativeButton={false}
                >
                  {t('home_modern.hero.cta_audit')}
                </Button>
              </BoltyWrapper>
            </Magnetic>
          </div>

          {/* Direct Proof Metrics */}
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 pt-6 sm:pt-8 border-t border-border/60 max-w-3xl mx-auto text-start">
            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <Gauge className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_speed_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_speed_sub')}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <Code2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_prop_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_prop_sub')}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_sec_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_sec_sub')}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{t('home_modern.hero.metric_local_title')}</div>
                <div className="text-xs text-muted-foreground">{t('home_modern.hero.metric_local_sub')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
